import { monthStartKey, toDateKey } from './date'

const STORAGE_KEY = 'resource-timesheet-demo-v2'

const SAMPLE_RESOURCES = [
  { id: 'demo-alessio', name: 'Alessio Nintendo', role: 'Bartender', email: 'alessio@example.com', vendor_name: 'Northstar Staffing', start_date: '2026-01-12', comments: 'Evening coverage and event shifts.' },
  { id: 'demo-annabelle', name: 'Annabelle Chen', role: 'Cleaner', email: 'annabelle@example.com', vendor_name: 'Direct', start_date: '2026-02-02', comments: '' },
  { id: 'demo-bob', name: 'Bob Hodgkins', role: 'Chef', email: 'bob@example.com', vendor_name: 'Culinary Partners', start_date: '2025-11-03', comments: 'Lead kitchen resource.' },
  { id: 'demo-daniella', name: 'Daniella Starr', role: 'Chef', email: 'daniella@example.com', vendor_name: 'Culinary Partners', start_date: '2026-03-16', comments: '' },
  { id: 'demo-kara', name: 'Kara Swisher', role: 'Server', email: 'kara@example.com', vendor_name: 'Direct', start_date: '2026-04-01', comments: '' },
  { id: 'demo-klaus', name: 'Klaus Shoenerkop', role: 'Chef', email: 'klaus@example.com', vendor_name: 'FoodWorks', start_date: '2026-01-05', comments: '' },
  { id: 'demo-ming', name: 'Ming Pan Dee', role: 'Bartender', email: 'ming@example.com', vendor_name: 'Northstar Staffing', start_date: '2026-05-18', comments: '' },
]

function randomId(prefix = 'demo') {
  if (globalThis.crypto?.randomUUID) return `${prefix}-${globalThis.crypto.randomUUID()}`
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function makeEntry(resourceId, date, status, hours = null, note = '') {
  return {
    id: randomId('entry'),
    resource_id: resourceId,
    work_date: toDateKey(date),
    status,
    hours,
    note,
    updated_at: new Date().toISOString(),
  }
}

function createSeedState() {
  const now = new Date()
  const first = new Date(now.getFullYear(), now.getMonth(), 1)
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
  const entries = []

  SAMPLE_RESOURCES.forEach((resource, resourceIndex) => {
    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(first.getFullYear(), first.getMonth(), day)
      const weekday = date.getDay()
      const isWeekend = weekday === 0 || weekday === 6
      const pattern = (day + resourceIndex * 3) % 13

      if (!isWeekend && pattern !== 0 && pattern !== 7) {
        entries.push(makeEntry(resource.id, date, 'worked', pattern === 5 ? 7.5 : 8))
      } else if (!isWeekend && pattern === 7) {
        entries.push(makeEntry(resource.id, date, 'leave'))
      }

      if (day === 12 + (resourceIndex % 3)) {
        const existingIndex = entries.findIndex(
          (entry) => entry.resource_id === resource.id && entry.work_date === toDateKey(date),
        )
        if (existingIndex >= 0) entries.splice(existingIndex, 1)
        entries.push(makeEntry(resource.id, date, resourceIndex % 2 === 0 ? 'sick' : 'holiday'))
      }
    }
  })

  return {
    resources: SAMPLE_RESOURCES.map((resource, index) => ({
      ...resource,
      active: true,
      sort_order: index * 10,
      created_at: new Date().toISOString(),
    })),
    entries,
    artifacts: [
      {
        id: randomId('artifact'),
        resource_id: 'demo-alessio',
        month_start: monthStartKey(now),
        month_starts: [monthStartKey(now)],
        kind: 'demo-file',
        label: 'Alessio-week-1.xlsx',
        external_url: null,
        storage_path: null,
        created_at: new Date().toISOString(),
      },
      {
        id: randomId('artifact'),
        resource_id: 'demo-bob',
        month_start: monthStartKey(now),
        month_starts: [monthStartKey(now)],
        kind: 'demo-file',
        label: 'Bob-week-1.xlsx',
        external_url: null,
        storage_path: null,
        created_at: new Date().toISOString(),
      },
    ],
  }
}

function readState() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      const seed = createSeedState()
      localStorage.setItem(STORAGE_KEY, JSON.stringify(seed))
      return seed
    }

    const parsed = JSON.parse(stored)
    if (!Array.isArray(parsed.resources) || !Array.isArray(parsed.entries)) {
      throw new Error('Invalid demo data')
    }
    parsed.resources = parsed.resources.map((resource) => ({
      ...resource,
      start_date: resource.start_date || null,
      vendor_name: resource.vendor_name || null,
      comments: resource.comments || null,
      resume_label: resource.resume_label || null,
      resume_storage_path: resource.resume_storage_path || null,
      resume_uploaded_at: resource.resume_uploaded_at || null,
    }))
    parsed.artifacts ||= []
    parsed.artifacts = parsed.artifacts.map((artifact) => ({
      ...artifact,
      month_starts:
        Array.isArray(artifact.month_starts) && artifact.month_starts.length
          ? artifact.month_starts
          : [artifact.month_start].filter(Boolean),
    }))
    return parsed
  } catch {
    const seed = createSeedState()
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seed))
    return seed
  }
}

function writeState(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

export function createDemoService() {
  return {
    async loadMonth(monthStart, range) {
      const state = readState()
      return {
        resources: state.resources
          .filter((resource) => resource.active !== false)
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name)),
        entries: state.entries.filter(
          (entry) => entry.work_date >= range.first && entry.work_date <= range.last,
        ),
        artifacts: state.artifacts.filter((artifact) => {
          const assignedMonths =
            Array.isArray(artifact.month_starts) && artifact.month_starts.length
              ? artifact.month_starts
              : [artifact.month_start]
          return assignedMonths.includes(monthStart)
        }),
      }
    },

    async saveEntry(payload) {
      const state = readState()
      const existingIndex = state.entries.findIndex(
        (entry) =>
          entry.resource_id === payload.resource_id && entry.work_date === payload.work_date,
      )

      if (!payload.status) {
        if (existingIndex >= 0) state.entries.splice(existingIndex, 1)
        writeState(state)
        return null
      }

      const saved = {
        ...(existingIndex >= 0 ? state.entries[existingIndex] : { id: randomId('entry') }),
        ...payload,
        hours: payload.status === 'worked' ? Number(payload.hours) : null,
        updated_at: new Date().toISOString(),
      }

      if (existingIndex >= 0) state.entries[existingIndex] = saved
      else state.entries.push(saved)

      writeState(state)
      return saved
    },

    async addResource(payload) {
      const state = readState()
      const resource = {
        id: randomId('resource'),
        name: payload.name.trim(),
        role: payload.role?.trim() || '',
        email: payload.email?.trim() || '',
        active: true,
        sort_order: state.resources.length * 10,
        start_date: payload.startDate || null,
        vendor_name: payload.vendorName?.trim() || null,
        comments: payload.comments?.trim() || null,
        resume_label: payload.resumeFile?.name || null,
        resume_storage_path: payload.resumeFile ? `demo-resume:${payload.resumeFile.name}` : null,
        resume_uploaded_at: payload.resumeFile ? new Date().toISOString() : null,
        created_at: new Date().toISOString(),
      }
      state.resources.push(resource)
      writeState(state)
      return resource
    },

    async updateResource(resource, payload) {
      const state = readState()
      const index = state.resources.findIndex((item) => item.id === resource.id)
      if (index < 0) throw new Error('Resource not found.')

      const updated = {
        ...state.resources[index],
        name: payload.name.trim(),
        role: payload.role?.trim() || '',
        email: payload.email?.trim() || '',
        start_date: payload.startDate || null,
        vendor_name: payload.vendorName?.trim() || null,
        comments: payload.comments?.trim() || null,
      }

      if (payload.resumeFile) {
        updated.resume_label = payload.resumeFile.name
        updated.resume_storage_path = `demo-resume:${payload.resumeFile.name}`
        updated.resume_uploaded_at = new Date().toISOString()
      } else if (payload.removeResume) {
        updated.resume_label = null
        updated.resume_storage_path = null
        updated.resume_uploaded_at = null
      }

      state.resources[index] = updated
      writeState(state)
      return updated
    },

    async getResourceResumeUrl() {
      return null
    },

    async uploadArtifact({ resourceId, monthStart, monthStarts, file }) {
      const state = readState()
      const assignedMonths = [...new Set([...(monthStarts || []), monthStart].filter(Boolean))].sort()
      const artifact = {
        id: randomId('artifact'),
        resource_id: resourceId,
        month_start: assignedMonths[0] || monthStart,
        month_starts: assignedMonths,
        kind: 'demo-file',
        label: file.name,
        external_url: null,
        storage_path: null,
        created_at: new Date().toISOString(),
      }
      state.artifacts.push(artifact)
      writeState(state)
      return artifact
    },

    async deleteArtifact(artifact) {
      const state = readState()
      state.artifacts = state.artifacts.filter((item) => item.id !== artifact.id)
      writeState(state)
    },

    async getArtifactUrl(artifact) {
      if (artifact.kind === 'link') return artifact.external_url
      return null
    },

    reset() {
      localStorage.removeItem(STORAGE_KEY)
      return readState()
    },
  }
}
